import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, Check, Eye, Pencil, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const BASE = window.location.origin;

export const ShareDialog = ({ open, onOpenChange, shareToken, viewToken }) => {
  const [copiedShare, setCopiedShare] = useState(false);
  const [copiedView, setCopiedView] = useState(false);
  const [copiedData, setCopiedData] = useState(false);

  const shareUrl = `${BASE}/furniture-projects/share/${shareToken}`;
  const viewUrl = `${BASE}/furniture-projects/view/${viewToken}`;
  // Stable, public JSON link intended for external tools (ChatGPT, scripts).
  // Returns pure JSON — no auth, no JavaScript required to parse.
  const dataLinkUrl = `${BASE}/api/public/furniture/${shareToken}`;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    if (type === 'share') {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    } else if (type === 'view') {
      setCopiedView(true);
      setTimeout(() => setCopiedView(false), 2000);
    } else if (type === 'data') {
      setCopiedData(true);
      toast.success('Project data link copied — paste it to ChatGPT');
      setTimeout(() => setCopiedData(false), 2500);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pencil className="h-4 w-4 text-[#2d948c]" />
              <label className="text-sm font-semibold text-[#1f2933]">Designer Link (Can Edit)</label>
            </div>
            <p className="text-xs text-[#5b6773] mb-2">Share this with designers so they can edit items and prices.</p>
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className="text-xs" data-testid="share-url-input" />
              <Button
                variant="outline" size="icon"
                onClick={() => copyToClipboard(shareUrl, 'share')}
                data-testid="copy-share-url"
              >
                {copiedShare ? <Check className="h-4 w-4 text-[#2f7d5a]" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-[#5b6773]" />
              <label className="text-sm font-semibold text-[#1f2933]">View-Only Link</label>
            </div>
            <p className="text-xs text-[#5b6773] mb-2">Share this for read-only access to the project.</p>
            <div className="flex gap-2">
              <Input value={viewUrl} readOnly className="text-xs" data-testid="view-url-input" />
              <Button
                variant="outline" size="icon"
                onClick={() => copyToClipboard(viewUrl, 'view')}
                data-testid="copy-view-url"
              >
                {copiedView ? <Check className="h-4 w-4 text-[#2f7d5a]" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="border-t pt-5">
            <div className="flex items-center gap-2 mb-2">
              <LinkIcon className="h-4 w-4 text-[#5b6773]" />
              <label className="text-sm font-semibold text-[#1f2933]">Project Data Link</label>
            </div>
            <p className="text-xs text-[#5b6773] mb-2">
              Paste this URL into ChatGPT or any external tool. It returns the
              project as plain JSON — every room, item, price, product link,
              item image, and a direct PDF URL for each room's mood boards.
              No login or JavaScript required.
            </p>
            <div className="flex gap-2">
              <Input value={dataLinkUrl} readOnly className="text-xs" data-testid="data-link-url-input" />
              <Button
                variant="outline" size="icon"
                onClick={() => copyToClipboard(dataLinkUrl, 'data')}
                data-testid="copy-data-link-url"
              >
                {copiedData ? <Check className="h-4 w-4 text-[#2f7d5a]" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
